import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchRecordingAsync,
  updateRecordingAsync,
  updateSegmentAsync,
  addSegmentAsync,
  deleteSegmentAsync,
  deleteRecordingAsync,
  getRecordingAudioUrlAsync,
  reTranscribeAsync,
  clearCurrent,
  RecordingSegment,
  TranscriptionMode
} from '../store/slices/recordingsSlice';
import {
  generateAiInsightsAsync,
  fetchAiInsightsJobAsync,
  ConsultationAiInsightsJob
} from '../store/slices/consultationsSlice';
import LoadingSpinner from './LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import ConfirmSpeakersModal from './ConfirmSpeakersModal';
import { getApiErrorMessage } from '../utils/apiError';
import './RecordingDetail.css';

const POLL_INTERVAL_MS = 10_000;

function formatTime(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function isTranscribing(status: string): boolean {
  return status === 'pending' || status === 'processing';
}

function slotLabel(mode: TranscriptionMode, slot: 1 | 2): string {
  if (mode === 'mixed') {
    return slot === 1 ? 'Speaker A (first detected)' : 'Speaker B (second detected)';
  }
  return slot === 1 ? 'OBS Track 2 (mic)' : 'OBS Track 3 (desktop)';
}

function modeLabel(mode: TranscriptionMode): string {
  return mode === 'mixed' ? 'Mixed track' : 'Multichannel';
}

const RecordingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const recordingId = parseInt(id || '0', 10);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const current = useAppSelector((state) => state.recordings.current);

  // Speaker mapping is locked once a consultation has any AI insights job. We
  // check this on mount and on consultation change; the backend still enforces
  // it (returns 409), but the UI nudges the user before they try.
  const [speakerLocked, setSpeakerLocked] = useState<boolean>(false);
  const [swapError, setSwapError] = useState<string | null>(null);

  // AI Insights generation state
  const [insightsJob, setInsightsJob] = useState<ConsultationAiInsightsJob | null>(null);
  const [insightsModalOpen, setInsightsModalOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const insightsPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [audioUrl, setAudioUrl] = useState<string>('');
  const [reRunModalOpen, setReRunModalOpen] = useState<boolean>(false);
  // -1 = adding at the top (no anchor segment); a segment id = adding after that segment.
  const [addingAfterId, setAddingAfterId] = useState<number | null>(null);
  const [deleteCandidateId, setDeleteCandidateId] = useState<number | null>(null);
  const [deleteRecordingModalOpen, setDeleteRecordingModalOpen] = useState<boolean>(false);
  const [deletingRecording, setDeletingRecording] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (recordingId) {
      dispatch(fetchRecordingAsync(recordingId));
    }
    return () => {
      dispatch(clearCurrent());
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [recordingId, dispatch]);

  const status = current?.status;

  // Poll while transcription is in flight
  useEffect(() => {
    if (!recordingId) return;
    if (status === 'pending' || status === 'processing') {
      pollRef.current = setInterval(() => {
        dispatch(fetchRecordingAsync(recordingId));
      }, POLL_INTERVAL_MS);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [status, recordingId, dispatch]);

  // Fetch a presigned audio URL once recording is past uploading state
  useEffect(() => {
    if (!recordingId || !status || status === 'uploading') return;
    dispatch(getRecordingAudioUrlAsync(recordingId))
      .unwrap()
      .then((res) => setAudioUrl(res.url))
      .catch(() => { /* ignore — banner already shows status */ });
  }, [recordingId, status, dispatch]);

  const refreshAudioUrl = useCallback(async () => {
    if (!recordingId) return;
    const wasPlaying = audioRef.current && !audioRef.current.paused;
    const currentTime = audioRef.current?.currentTime || 0;
    try {
      const res = await dispatch(getRecordingAudioUrlAsync(recordingId)).unwrap();
      setAudioUrl(res.url);
      requestAnimationFrame(() => {
        if (audioRef.current) {
          audioRef.current.currentTime = currentTime;
          if (wasPlaying) audioRef.current.play().catch(() => { /* ignore */ });
        }
      });
    } catch (_) {
      // ignore
    }
  }, [dispatch, recordingId]);

  const seekTo = useCallback((startMs: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = startMs / 1000;
    audioRef.current.play().catch(() => { /* ignore */ });
  }, []);

  const speakerLabel = useCallback((channel: number) => {
    if (channel === 0) return 'Mix';
    if (!current) return channel === 1 ? 'Teacher' : 'Client';
    return channel === current.channelTeacher ? 'Teacher' : 'Client';
  }, [current]);


  const handleInsightsGenerate = useCallback(async () => {
    if (!current?.consultationId) return;
    setGenerating(true);
    setGenError(null);
    try {
      const job = await dispatch(generateAiInsightsAsync(current.consultationId)).unwrap();
      setInsightsJob(job);
      setSpeakerLocked(true);
      setInsightsModalOpen(false);
    } catch (err) {
      setGenError(typeof err === 'string' ? err : 'Failed to start insights job');
    } finally {
      setGenerating(false);
    }
  }, [current, dispatch]);

  const handleInsightsSwap = useCallback(async () => {
    if (!current) return;
    setSwapError(null);
    const next = current.channelTeacher === 1 ? 2 : 1;
    try {
      await dispatch(updateRecordingAsync({ id: current.id, patch: { channelTeacher: next } })).unwrap();
      dispatch(fetchRecordingAsync(current.id));
    } catch (err) {
      const msg = typeof err === 'string' ? err : getApiErrorMessage(err, 'Failed to swap speakers');
      setSwapError(msg);
    }
  }, [current, dispatch]);


  const handleChannelTeacherChange = useCallback(async (value: 1 | 2) => {
    if (!current) return;
    setSwapError(null);
    try {
      await dispatch(updateRecordingAsync({ id: current.id, patch: { channelTeacher: value } })).unwrap();
    } catch (err) {
      // Backend returns 409 { code: 'speaker_mapping_locked' } when an AI
      // insights job exists. Surface inline and lock the UI for next time.
      const msg = typeof err === 'string' ? err : getApiErrorMessage(err, 'Failed to swap speakers');
      setSwapError(msg);
      if (/locked/i.test(msg)) setSpeakerLocked(true);
    }
  }, [current, dispatch]);

  // Refresh the lock flag and insights job whenever the consultation changes.
  useEffect(() => {
    const consultationId = current?.consultationId;
    let cancelled = false;
    if (!consultationId) {
      // Clear stale lock/job state for the (now absent) consultation. Done in a
      // microtask so the reset is not a synchronous setState in the effect body.
      queueMicrotask(() => {
        if (cancelled) return;
        setSpeakerLocked(false);
        setInsightsJob(null);
      });
      return () => { cancelled = true; };
    }
    dispatch(fetchAiInsightsJobAsync(consultationId))
      .unwrap()
      .then((job) => {
        if (cancelled) return;
        setInsightsJob(job);
        setSpeakerLocked(!!job);
      })
      .catch(() => { /* leave lock as-is on transient failure */ });
    return () => { cancelled = true; };
  }, [current?.consultationId, dispatch]);

  // Poll while an insights job is pending/processing.
  const insightsRunning = !!(insightsJob && (insightsJob.status === 'pending' || insightsJob.status === 'processing'));
  useEffect(() => {
    const consultationId = current?.consultationId;
    if (!insightsRunning || !consultationId) {
      if (insightsPollRef.current) { clearInterval(insightsPollRef.current); insightsPollRef.current = null; }
      return;
    }
    insightsPollRef.current = setInterval(async () => {
      const latest = await dispatch(fetchAiInsightsJobAsync(consultationId)).unwrap().catch(() => null);
      if (latest) {
        setInsightsJob(latest);
        setSpeakerLocked(true);
      }
    }, 15_000);
    return () => { if (insightsPollRef.current) { clearInterval(insightsPollRef.current); insightsPollRef.current = null; } };
  }, [insightsRunning, current?.consultationId, dispatch]);

  const handleTranscriptionModeChange = useCallback((value: TranscriptionMode) => {
    if (!current || current.transcriptionMode === value) return;
    dispatch(updateRecordingAsync({ id: current.id, patch: { transcriptionMode: value } }));
  }, [current, dispatch]);

  const handleHealthyChange = useCallback((value: boolean) => {
    if (!current || current.healthy === value) return;
    dispatch(updateRecordingAsync({ id: current.id, patch: { healthy: value } }));
  }, [current, dispatch]);

  const handleConfirmReRun = useCallback(() => {
    if (!current) return;
    setReRunModalOpen(false);
    dispatch(reTranscribeAsync({ id: current.id, transcriptionMode: current.transcriptionMode }));
  }, [current, dispatch]);

  const handleConfirmDeleteRecording = useCallback(async () => {
    if (!current) return;
    setDeletingRecording(true);
    try {
      const consultationId = current.consultationId;
      await dispatch(deleteRecordingAsync(current.id)).unwrap();
      navigate(`/consultations/edit/${consultationId}`);
    } catch (_) {
      setDeletingRecording(false);
      // modal stays open; error surfaces via slice state
    }
  }, [current, dispatch, navigate]);

  const segments: RecordingSegment[] = useMemo(() => {
    if (!current?.segments) return [];
    return [...current.segments]
      .filter(s => s.channel !== 0)
      .sort((a, b) => a.startMs - b.startMs);
  }, [current]);

  const sampleQuotes = useMemo(() => {
    if (!segments.length) return null;
    // The channel's first turn: newer runs store one row per sentence, so a
    // single row is often just «Алло.».
    const firstTurn = (channel: 1 | 2) => {
      const start = segments.findIndex(s => s.channel === channel);
      if (start === -1) return null;
      const parts: string[] = [];
      for (let i = start; i < segments.length && segments[i].channel === channel; i++) {
        parts.push(segments[i].text);
      }
      return parts.join(' ');
    };
    const trunc = (t: string | null) => {
      if (t == null) return null;
      const clean = t.replace(/\s+/g, ' ').trim();
      return clean.length > 80 ? clean.slice(0, 79) + '…' : clean;
    };
    return {
      channel1: trunc(firstTurn(1)),
      channel2: trunc(firstTurn(2)),
    };
  }, [segments]);

  const activeJob = useMemo(() => {
    if (!current?.jobs || !current.jobs.length) return null;
    if (current.activeJobId) {
      return current.jobs.find(j => j.id === current.activeJobId) || null;
    }
    return null;
  }, [current]);

  const olderRunsCount = useMemo(() => {
    if (!current?.jobs || !activeJob) return (current?.jobs?.filter(j => j.status === 'completed').length || 0);
    return current.jobs.filter(j => j.status === 'completed' && j.id !== activeJob.id).length;
  }, [current, activeJob]);

  // The mode actually used by the currently-shown transcript (the active run's
  // mode snapshot, falling back to the recording's mode if no active run exists).
  const shownMode: TranscriptionMode = activeJob?.transcriptionMode || current?.transcriptionMode || 'multichannel';


  if (!current) return <LoadingSpinner />;

  const consultationLabel = current.consultation
    ? `${current.consultation.firstName || ''} ${current.consultation.lastName || ''}`.trim() || `Consultation #${current.consultation.id}`
    : `Consultation #${current.consultationId}`;

  const durationLabel = current.durationSeconds != null
    ? `${Math.floor(current.durationSeconds / 60)}m ${current.durationSeconds % 60}s`
    : null;

  return (
    <div className="recording-detail">
      <div className="recording-detail__header">
        <div>
          <Link to="/recordings" className="recording-detail__back">← All recordings</Link>
          <h1>Recording #{current.id}</h1>
          <div className="recording-detail__subtitle">
            <Link to={`/consultations/edit/${current.consultationId}`}>{consultationLabel}</Link>
            {durationLabel && <span className="recording-detail__dot">•</span>}
            {durationLabel && <span>{durationLabel}</span>}
          </div>
        </div>
        <div className="recording-detail__header-actions">
          <StatusPill status={current.status} />
          <button
            type="button"
            className="recording-detail__delete-btn"
            onClick={() => setDeleteRecordingModalOpen(true)}
            title="Delete this recording"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
            <span>Delete</span>
          </button>
        </div>
      </div>

      <StatusBanner status={current.status} lastError={current.job?.lastError} />

      {audioUrl && current.status !== 'uploading' && (
        <div className="recording-detail__audio-card">
          <audio
            ref={audioRef}
            src={audioUrl}
            controls
            className="recording-detail__audio"
            onError={refreshAudioUrl}
          />
        </div>
      )}

      <section className="recording-detail__card">
        <div className="recording-detail__card-head">
          <h2 className="recording-detail__card-title">Transcription mode</h2>
          <button
            type="button"
            className="transcription-mode__rerun"
            onClick={() => setReRunModalOpen(true)}
            disabled={isTranscribing(current.status) || current.status === 'uploading'}
            title={isTranscribing(current.status)
              ? 'A transcription job is already running'
              : 'Re-run transcription with the selected mode. Previous runs are kept in the database; only the newest one is shown.'}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            <span>Re-run</span>
          </button>
        </div>
        <p className="recording-detail__card-hint">
          Pick a mode then press Re-run to re-decode the audio. Older runs stay in the database;
          only the most recent successful one is shown above.
        </p>
        <div className="transcription-mode__toggle" role="tablist" aria-label="Transcription mode">
          <button
            type="button"
            role="tab"
            aria-selected={current.transcriptionMode === 'multichannel'}
            className={`transcription-mode__btn${current.transcriptionMode === 'multichannel' ? ' is-active' : ''}`}
            onClick={() => handleTranscriptionModeChange('multichannel')}
            disabled={isTranscribing(current.status)}
          >
            <span className="transcription-mode__title">Multichannel <span className="transcription-mode__tag">Recommended</span></span>
            <span className="transcription-mode__sub">mic + desktop tracks transcribed separately</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={current.transcriptionMode === 'mixed'}
            className={`transcription-mode__btn${current.transcriptionMode === 'mixed' ? ' is-active' : ''}`}
            onClick={() => handleTranscriptionModeChange('mixed')}
            disabled={isTranscribing(current.status)}
            title="Slightly less reliable — use only if the mic track has noise issues (e.g. laptop speakers picked up). Re-run after switching."
          >
            <span className="transcription-mode__title">Mixed track</span>
            <span className="transcription-mode__sub">mix track only, speakers split by diarization</span>
          </button>
        </div>
      </section>

      <section className="recording-detail__card">
        <h2 className="recording-detail__card-title">Speaker channels</h2>
        <p className="recording-detail__card-hint">
          {shownMode === 'mixed'
            ? "Which diarized speaker is the teacher and which is the client. If the labels look swapped, click Swap."
            : "Which track holds your voice and which holds the client's. If transcripts come out with speakers swapped, click Swap."}
        </p>
        <div className="channel-map">
          <div className="channel-map__row channel-map__row--teacher">
            <span className="channel-map__role">Teacher</span>
            <span className="channel-map__arrow" aria-hidden>→</span>
            <span className="channel-map__track">{slotLabel(shownMode, current.channelTeacher)}</span>
          </div>
          <div className="channel-map__row channel-map__row--client">
            <span className="channel-map__role">Client</span>
            <span className="channel-map__arrow" aria-hidden>→</span>
            <span className="channel-map__track">{slotLabel(shownMode, current.channelTeacher === 1 ? 2 : 1)}</span>
          </div>
          {!speakerLocked && (
            <button
              type="button"
              className="channel-map__swap"
              onClick={() => handleChannelTeacherChange(current.channelTeacher === 1 ? 2 : 1)}
              title="Swap teacher and client tracks"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="17 1 21 5 17 9" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <polyline points="7 23 3 19 7 15" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
              <span>Swap</span>
            </button>
          )}
        </div>
        {swapError && (
          <p style={{ marginTop: 8, color: '#c0392b', fontSize: 13 }}>{swapError}</p>
        )}
      </section>

      {current.status === 'completed' && (
        <section className="recording-detail__card">
          <h2 className="recording-detail__card-title">Transcript health</h2>
          <p className="recording-detail__card-hint">
            {current.healthCheckedAt
              ? `AI check ${current.healthy ? 'passed' : 'flagged a leak'} on ${new Date(current.healthCheckedAt).toLocaleDateString()}.` +
                (current.healthReason ? ` ${current.healthReason}` : '')
              : 'Not auto-checked yet. Untick if client speech leaked into the teacher track (recorded without headphones).'}
          </p>
          <label className="checkbox-control">
            <input
              type="checkbox"
              checked={current.healthy}
              onChange={(e) => handleHealthyChange(e.target.checked)}
            />
            <span className="checkbox-control__label">Transcript is healthy (no cross-track audio leak)</span>
          </label>
          {!current.healthy && (
            <p style={{ marginTop: 10, marginBottom: 0, padding: '8px 10px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 'var(--radius)', color: '#92400e', fontSize: 13 }}>
              Tip: re-run transcription in Mixed track mode — diarization on the mix track is
              immune to the leak. A new run resets this flag and re-checks automatically.
            </p>
          )}
        </section>
      )}

      <section className="recording-detail__card">
        <div className="recording-detail__card-head">
          <h2 className="recording-detail__card-title">AI Insights</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {insightsJob?.status === 'failed' && (
              <span style={{ fontSize: 11, color: '#c0392b' }}>Failed</span>
            )}
            {insightsJob?.status === 'completed' && (
              <span style={{ fontSize: 11, color: '#888' }}>
                Last run {insightsJob.completedAt ? new Date(insightsJob.completedAt).toLocaleString() : ''}
              </span>
            )}
            <button
              type="button"
              className="btn-primary"
              onClick={() => { setGenError(null); setSwapError(null); setInsightsModalOpen(true); }}
              disabled={!current.activeJobId || insightsRunning || current.status !== 'completed'}
              title={
                current.status !== 'completed'
                  ? 'Wait for transcription to complete'
                  : !current.activeJobId
                    ? 'No completed transcript'
                    : ''
              }
              style={{ fontSize: 13, padding: '5px 12px' }}
            >
              {insightsRunning ? 'Processing…' : insightsJob ? 'Re-run' : 'Generate'}
            </button>
          </div>
        </div>
        {!current.activeJobId && current.status === 'completed' && (
          <p className="recording-detail__card-hint" style={{ marginBottom: 0 }}>
            No completed transcript yet — transcribe first to enable AI insights.
          </p>
        )}
        {insightsRunning && (
          <p className="recording-detail__card-hint" style={{ marginBottom: 0 }}>
            <span className="recording-detail__spinner" aria-hidden /> Generating insights — this may take a minute or two.
          </p>
        )}
        {insightsJob?.status === 'failed' && insightsJob.lastError && (
          <div style={{ marginTop: 8, padding: 8, background: '#fdecea', border: '1px solid #f5b7b1', borderRadius: 'var(--radius)', color: '#922b21', fontSize: 12 }}>
            <strong>Last run failed:</strong> {insightsJob.lastError}
          </div>
        )}
        {genError && (
          <div style={{ marginTop: 8, color: '#c0392b', fontSize: 12 }}>{genError}</div>
        )}
        {insightsJob?.status === 'completed' && (
          <p className="recording-detail__card-hint" style={{ marginBottom: 0 }}>
            Insights generated.{' '}
            <Link to={`/consultations/edit/${current.consultationId}`} style={{ color: '#2563eb' }}>
              View &amp; edit on the consultation page
            </Link>
          </p>
        )}
      </section>

      <ConfirmSpeakersModal
        isOpen={insightsModalOpen}
        channelTeacher={current.channelTeacher}
        sampleQuotes={sampleQuotes}
        locked={speakerLocked}
        hasPriorData={!!insightsJob}
        onSwap={handleInsightsSwap}
        onGenerate={handleInsightsGenerate}
        onCancel={() => setInsightsModalOpen(false)}
        submitting={generating}
        swapError={swapError}
        unhealthy={current.healthy === false}
        unhealthyReason={current.healthReason}
      />

      <section className="recording-detail__card">
        <div className="recording-detail__transcript-head">
          <h2 className="recording-detail__card-title">Transcript</h2>
          <div className="recording-detail__transcript-meta">
            {activeJob && (
              <span className="recording-detail__run-badge" title={`Showing transcription run #${activeJob.runIndex} (${modeLabel(activeJob.transcriptionMode)})`}>
                Run #{activeJob.runIndex} · {modeLabel(activeJob.transcriptionMode)}
              </span>
            )}
            {olderRunsCount > 0 && (
              <span className="recording-detail__run-badge recording-detail__run-badge--muted" title="Older runs are kept in the database but not shown.">
                +{olderRunsCount} older
              </span>
            )}
            {segments.length > 0 && (
              <span className="recording-detail__transcript-count">{segments.length} segments</span>
            )}
          </div>
        </div>
        {isTranscribing(current.status) && (
          <p className="recording-detail__transcript-pending">
            <span className="recording-detail__spinner" aria-hidden />
            Transcription in progress — this usually takes a few minutes.
            {segments.length > 0 && <> Showing the previous run below in the meantime.</>}
          </p>
        )}
        {!isTranscribing(current.status) && segments.length === 0 && current.status === 'completed' && (
          <div className="recording-detail__transcript-empty-wrap">
            <p className="recording-detail__transcript-empty">No segments in this run.</p>
            {addingAfterId !== -1 && (
              <button
                type="button"
                className="recording-detail__add-first"
                onClick={() => setAddingAfterId(-1)}
                disabled={!current.activeJobId}
                title={current.activeJobId ? 'Add the first transcript block' : 'No active transcription run yet'}
              >
                + Add segment
              </button>
            )}
            {addingAfterId === -1 && (
              <AddSegmentForm
                defaultChannel={current.channelTeacher === 1 ? 1 : 2}
                defaultStartMs={0}
                channelTeacher={current.channelTeacher}
                shownMode={shownMode}
                onCancel={() => setAddingAfterId(null)}
                onSave={async (payload) => {
                  await dispatch(addSegmentAsync({ recordingId: current.id, ...payload })).unwrap();
                  setAddingAfterId(null);
                }}
              />
            )}
          </div>
        )}
        {segments.length > 0 && (
          <ol className="recording-detail__segments">
            {segments.map((seg, idx) => {
              const prev = segments[idx - 1];
              const next = segments[idx + 1];
              const nextStartMs = next ? next.startMs : seg.endMs + 2000;
              const midpoint = Math.round((seg.endMs + nextStartMs) / 2);
              const oppositeChannel: 1 | 2 = seg.channel === 1 ? 2 : 1;
              return (
                <React.Fragment key={seg.id}>
                  <SegmentRow
                    segment={seg}
                    speakerLabel={speakerLabel(seg.channel)}
                    continuesTurn={prev != null && prev.channel === seg.channel}
                    canEdit={!isTranscribing(current.status)}
                    onSeek={() => seekTo(seg.startMs)}
                    onSave={(text) =>
                      dispatch(updateSegmentAsync({ recordingId: current.id, segmentId: seg.id, text }))
                    }
                    onDelete={() => setDeleteCandidateId(seg.id)}
                    onRequestAdd={() => setAddingAfterId(seg.id)}
                  />
                  {addingAfterId === seg.id && (
                    <li className="recording-detail__segment-form-wrap">
                      <AddSegmentForm
                        defaultChannel={oppositeChannel}
                        defaultStartMs={midpoint}
                        channelTeacher={current.channelTeacher}
                        shownMode={shownMode}
                        onCancel={() => setAddingAfterId(null)}
                        onSave={async (payload) => {
                          await dispatch(addSegmentAsync({ recordingId: current.id, ...payload })).unwrap();
                          setAddingAfterId(null);
                        }}
                      />
                    </li>
                  )}
                </React.Fragment>
              );
            })}
          </ol>
        )}
      </section>

      <ConfirmationModal
        isOpen={reRunModalOpen}
        title="Re-run transcription?"
        message={`This will discard the current transcript (${segments.length} segment${segments.length === 1 ? '' : 's'}) and re-transcribe in ${modeLabel(current.transcriptionMode)} mode. Continue?`}
        confirmText="Re-run"
        onConfirm={handleConfirmReRun}
        onCancel={() => setReRunModalOpen(false)}
        dangerous={true}
      />

      <ConfirmationModal
        isOpen={deleteRecordingModalOpen}
        title="Delete this recording?"
        message={`This will permanently delete recording #${current.id}, its audio files, and all transcripts. This cannot be undone.`}
        confirmText={deletingRecording ? 'Deleting…' : 'Delete recording'}
        cancelText="Cancel"
        onConfirm={handleConfirmDeleteRecording}
        onCancel={() => { if (!deletingRecording) setDeleteRecordingModalOpen(false); }}
        dangerous={true}
      />

      <ConfirmationModal
        isOpen={deleteCandidateId !== null}
        title="Delete this block?"
        message={(() => {
          const seg = segments.find(s => s.id === deleteCandidateId);
          if (!seg) return 'Delete this transcript block?';
          const preview = seg.text.length > 120 ? `${seg.text.slice(0, 120)}…` : seg.text;
          return `Delete the ${speakerLabel(seg.channel).toLowerCase()} block at ${formatTime(seg.startMs)}?\n\n"${preview}"`;
        })()}
        confirmText="Delete"
        onConfirm={() => {
          if (deleteCandidateId != null) {
            dispatch(deleteSegmentAsync({ recordingId: current.id, segmentId: deleteCandidateId }));
          }
          setDeleteCandidateId(null);
        }}
        onCancel={() => setDeleteCandidateId(null)}
        dangerous={true}
      />
    </div>
  );
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; cls: string }> = {
    uploading: { label: 'Uploading', cls: 'uploading' },
    pending: { label: 'Queued', cls: 'pending' },
    processing: { label: 'Transcribing', cls: 'processing' },
    completed: { label: 'Completed', cls: 'completed' },
    failed: { label: 'Failed', cls: 'failed' },
  };
  const entry = map[status] || { label: status, cls: 'unknown' };
  return <span className={`recording-detail__status-pill recording-detail__status-pill--${entry.cls}`}>{entry.label}</span>;
};

interface StatusBannerProps {
  status: string;
  lastError?: string | null;
}

const StatusBanner: React.FC<StatusBannerProps> = ({ status, lastError }) => {
  if (status === 'failed') {
    return (
      <div className="recording-detail__banner recording-detail__banner--error">
        <div className="recording-detail__banner-body">
          <strong>Transcription failed.</strong>
          {lastError && <div className="recording-detail__error-detail">{lastError}</div>}
          <div className="recording-detail__banner-hint">
            Use the <em>Re-run transcription</em> button below to try again — switch the mode first if the mic track was the issue.
          </div>
        </div>
      </div>
    );
  }
  return null;
};

interface SegmentRowProps {
  segment: RecordingSegment;
  speakerLabel: string;
  // Same speaker as the row above: shown as one turn, speaker label hidden.
  continuesTurn: boolean;
  canEdit: boolean;
  onSeek: () => void;
  onSave: (text: string) => void;
  onDelete: () => void;
  onRequestAdd: () => void;
}

const SegmentRow: React.FC<SegmentRowProps> = ({ segment, speakerLabel, continuesTurn, canEdit, onSeek, onSave, onDelete, onRequestAdd }) => {
  const [draft, setDraft] = useState<string>(segment.text);
  // Reset the editable draft when the segment text changes externally (e.g.
  // after a save or re-fetch). Adjusting state during render is the React-
  // recommended alternative to a syncing effect.
  const [lastText, setLastText] = useState<string>(segment.text);
  if (segment.text !== lastText) {
    setLastText(segment.text);
    setDraft(segment.text);
  }
  const dirty = draft !== segment.text;

  const handleBlur = () => {
    if (dirty) onSave(draft);
  };

  return (
    <li className={`recording-detail__segment${continuesTurn ? ' recording-detail__segment--continued' : ''}`}>
      <button className="recording-detail__segment-time" onClick={onSeek} title="Play from here">
        {formatTime(segment.startMs)}
      </button>
      <span
        className={`recording-detail__segment-speaker recording-detail__segment-speaker--${speakerLabel.toLowerCase()}`}
        aria-hidden={continuesTurn || undefined}
      >
        {speakerLabel}
      </span>
      <textarea
        className="recording-detail__segment-text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        rows={Math.max(1, Math.ceil(draft.length / 100))}
      />
      {canEdit && (
        <button
          type="button"
          className="recording-detail__segment-delete"
          onClick={onDelete}
          title="Delete this block"
          aria-label="Delete this block"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </button>
      )}
      {canEdit && (
        <button
          type="button"
          className="recording-detail__segment-add"
          onClick={onRequestAdd}
          title="Insert a new block after this one"
          aria-label="Insert a new block after this one"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      )}
    </li>
  );
};

interface AddSegmentFormProps {
  defaultChannel: 1 | 2;
  defaultStartMs: number;
  channelTeacher: 1 | 2;
  shownMode: TranscriptionMode;
  onSave: (payload: { channel: 1 | 2; startMs: number; endMs: number; text: string }) => Promise<void> | void;
  onCancel: () => void;
}

function parseTimeString(input: string): number | null {
  // Accepts m:ss, mm:ss, mm:ss.S, or plain integer seconds.
  const trimmed = input.trim();
  if (!trimmed) return null;
  const colon = trimmed.indexOf(':');
  if (colon === -1) {
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 1000);
  }
  const m = Number(trimmed.slice(0, colon));
  const s = Number(trimmed.slice(colon + 1));
  if (!Number.isFinite(m) || !Number.isFinite(s) || m < 0 || s < 0 || s >= 60) return null;
  return Math.round((m * 60 + s) * 1000);
}

function formatTimeStr(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const AddSegmentForm: React.FC<AddSegmentFormProps> = ({ defaultChannel, defaultStartMs, channelTeacher, shownMode, onSave, onCancel }) => {
  const [channel, setChannel] = useState<1 | 2>(defaultChannel);
  const [timeStr, setTimeStr] = useState<string>(formatTimeStr(defaultStartMs));
  const [text, setText] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);

  // Channel 1/2 → Teacher/Client based on the recording's channelTeacher.
  const labelFor = (ch: 1 | 2): string => {
    const role = ch === channelTeacher ? 'Teacher' : 'Client';
    if (shownMode === 'mixed') return role === 'Teacher' ? 'Speaker A (teacher)' : 'Speaker B (client)';
    return role === 'Teacher' ? 'Teacher (mic)' : 'Client (desktop)';
  };

  const handleSave = async () => {
    const startMs = parseTimeString(timeStr);
    if (startMs == null) { setError('Invalid time. Use mm:ss (e.g. 1:23) or seconds.'); return; }
    if (!text.trim()) { setError('Text is required.'); return; }
    setError('');
    setBusy(true);
    try {
      // Rough endMs heuristic: ~70ms per character, min 1s, max 30s.
      const endMs = startMs + Math.min(Math.max(text.length * 70, 1000), 30000);
      await onSave({ channel, startMs, endMs, text: text.trim() });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to add segment.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="add-segment">
      <div className="add-segment__row">
        <div className="add-segment__speaker" role="tablist" aria-label="Speaker">
          {[1, 2].map((ch) => (
            <button
              key={ch}
              type="button"
              role="tab"
              aria-selected={channel === ch}
              className={`add-segment__speaker-btn${channel === ch ? ' is-active' : ''}`}
              onClick={() => setChannel(ch as 1 | 2)}
              disabled={busy}
            >
              {labelFor(ch as 1 | 2)}
            </button>
          ))}
        </div>
        <label className="add-segment__time-wrap" title="When this block starts (mm:ss)">
          <span className="add-segment__time-label">At</span>
          <input
            type="text"
            inputMode="numeric"
            className="add-segment__time-input"
            value={timeStr}
            onChange={(e) => setTimeStr(e.target.value)}
            placeholder="0:00"
            disabled={busy}
          />
        </label>
      </div>
      <textarea
        className="add-segment__text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What was said?"
        rows={2}
        autoFocus
        disabled={busy}
      />
      {error && <div className="add-segment__error">{error}</div>}
      <div className="add-segment__actions">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn-success" onClick={handleSave} disabled={busy}>Add</button>
      </div>
    </div>
  );
};

export default RecordingDetail;
